<template>
  <div class="tile-details" :class="enabled ? 'tile-details--enabled' : 'tile-details--disabled'">
    <div class="tile-details__image-wrapper" :style="`${backgroundColorCssComputed} ${radialGradientCssComputed}`">
      <img :src="imageUrl" :alt="prompt" :title="prompt" loading="lazy" class="tile-details__image" />
    </div>
    <div class="tile-details__row-wrapper">
      <div class="tile-details__row">
        <h4 class="tile-details__property">Index</h4>
        <p class="tile-details__value">{{ index }}</p>
      </div>
      <div class="tile-details__row">
        <h4 class="tile-details__property">Prompt</h4>
        <p class="tile-details__value">{{ prompt }}</p>
      </div>
      <div class="tile-details__row tile-details__row--tile-id" :class="`${tileIdIsInClipboard ? 'in-clipboard' : ''}`" @click="onTileIdClick" title="Copy to clipboard">
        <h4 class="tile-details__property">Tile ID</h4>
        <p class="tile-details__value tile-id">{{ tileId }}</p>
      </div>
      <div class="tile-details__row">
        <h4 class="tile-details__property">Enabled</h4>
        <p class="tile-details__value">{{ enabled }}<span class="enabled-indicator" :class="enabledModifierClass"></span></p>
      </div>
      <div class="tile-details__row">
        <h4 class="tile-details__property">Answer text</h4>
        <p class="tile-details__value">{{ answerText }}</p>
      </div>
      <div class="tile-details__row">
        <h4 class="tile-details__property">Background colour</h4>
        <p class="tile-details__value">{{ backgroundColor }}<span v-if="backgroundColor" class="background-color-chip" :style="colorChipInlineStyle"></span></p>
      </div>
      <div class="tile-details__row">
        <h4 class="tile-details__property">Radial gradient hex colour</h4>
        <p class="tile-details__value">
          {{ radialGradientHexColor }}<span v-if="radialGradientHexColor" class="radial-gradient-color-chip" :style="colorChipRadialGradientInlineStyle"></span>
        </p>
      </div>
      <div class="tile-details__row">
        <h4 class="tile-details__property">Image filename</h4>
        <p class="tile-details__value">{{ imageFilename }}</p>
      </div>
      <div class="tile-details__row  tile-details__row--last">
        <h4 class="tile-details__property">Image URL</h4>
        <p class="tile-details__value">
          <a :href="imageUrl" target="_blank" title="Open image in a new tab">{{ imageUrl }}</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import {COLOR_SCHEMES, PATH_TO_TILE_IMAGES} from './../constants/globalConstants';
import getRadialGradientCss from './../helpers/getRadialGradientCss';
import copyToClipboard from './../helpers/copyToClipboard';

const userVuexModule = createNamespacedHelpers('userVuexModule');

export default {
  name: 'ViewBoardTile',
  props: {
    answerText: String,
    backgroundColor: String,
    enabled: Boolean,
    imageFilename: String,
    imagesFolderName: String,
    index: Number,
    invertTileImagesInDarkMode: Boolean,
    previewWithBackgrounds: Boolean,
    prompt: String,
    radialGradientHexColor: String,
    tileBackgroundColor: String,
    tileIdCopiedToClipboard: String,
    tileRadialGradientHexColor: String,
    tileId: String,
  },
  data() {
    return {};
  },
  computed: {
    ...userVuexModule.mapGetters(['getPrefersColorScheme']),
    tileIdIsInClipboard: function() {
      return this.tileId === this.tileIdCopiedToClipboard;
    },
    imageUrl: function() {
      return `${PATH_TO_TILE_IMAGES}${this.imagesFolderName}/${this.imageFilename}`;
    },
    enabledModifierClass: function() {
      if (this.enabled) {
        return 'enabled-indicator--enabled';
      } else {
        return 'enabled-indicator--disabled';
      }
    },
    colorChipInlineStyle: function() {
      if (this.backgroundColor) {
        return `background-color: ${this.backgroundColor};`;
      } else {
        return '';
      }
    },
    colorChipRadialGradientInlineStyle: function() {
      if (this.radialGradientHexColor) {
        return `background-color: ${this.radialGradientHexColor};`;
      } else {
        return '';
      }
    },
    backgroundColorCssComputed: function() {
      if (this.previewWithBackgrounds && this.backgroundColor) {
        return `background-color: ${this.backgroundColor};`;
      } else if (this.previewWithBackgrounds && this.tileBackgroundColor) {
        return `background-color: ${this.tileBackgroundColor};`;
      } else if (this.previewWithBackgrounds && this.getPrefersColorScheme === COLOR_SCHEMES.DARK.CONSTANT) {
        return `background-color: black;`;
      } else if (this.previewWithBackgrounds && this.getPrefersColorScheme === COLOR_SCHEMES.LIGHT.CONSTANT) {
        return `background-color: white;`;
      } else {
        return '';
      }
    },
    radialGradientCssComputed: function() {
      if (this.previewWithBackgrounds && this.radialGradientHexColor) {
        return getRadialGradientCss(this.radialGradientHexColor);
      } else if (this.previewWithBackgrounds && this.tileRadialGradientHexColor) {
        return getRadialGradientCss(this.tileRadialGradientHexColor);
      } else {
        return '';
      }
    },
  },
  methods: {
    onTileIdClick: function() {
      copyToClipboard(this.tileId);
      this.$emit('copied-to-clipboard', this.tileId);
    },
  },
};
</script>

<style lang="scss" scoped>
.tile-details {
  align-items: center;
  background-color: var(--color-form-element-background);
  border-radius: var(--border-radius);
  display: flex;
  flex-direction: column;
  font-size: 1.4rem;
  margin: 0 auto;
  max-width: var(--max-width-main-content);
  padding: var(--padding-s);

  @include breakpoint($mq-md-lg) {
    align-items: flex-start;
    flex-direction: row;
  }

  &__image-wrapper {
    border-radius: var(--border-radius);
    overflow: hidden;
    margin: 0 0 1rem 0;
    max-width: 22rem;

    @include breakpoint($mq-md-lg) {
      margin: 0 1rem 0 0;
      max-width: 100%;
      width: 30%;
    }
  }

  &__image {
    display: block;
    height: auto;
    width: 100%;
  }

  &__row-wrapper {
    width: 100%;

    @include breakpoint($mq-md-lg) {
      width: 70%;
    }
  }

  &__row {
    display: flex;
    margin-bottom: 0.5rem;
    min-height: 2.1rem;

    @include breakpoint($mq-md-lg) {
      margin-bottom: 0.75rem;
    }

    @include breakpoint($mq-lg) {
      margin-bottom: 1rem;
    }

    &--last {
      margin-bottom: 0;

      @include breakpoint($mq-md-lg) {
        margin-bottom: 0;
      }

      @include breakpoint($mq-lg) {
        margin-bottom: 0;
      }
    }

    &--tile-id {
      cursor: pointer;

      &.in-clipboard {
        h4,
        p {
          color: var(--color-tile-id-copied-to-clipboard);
        }
      }
    }
  }

  &__property {
    font-size: 1.4rem;
    margin: 0;
    width: 50%;

    @include breakpoint($mq-sm) {
      width: 40%;
    }

    @include breakpoint($mq-sm-md) {
      width: 33%;
    }

    @include breakpoint($mq-md) {
      width: 30%;
    }
  }

  &__value {
    margin: 0;
    overflow: hidden;
    width: 50%;
    word-break: break-all;

    @include breakpoint($mq-sm) {
      width: 60%;
    }

    @include breakpoint($mq-sm-md) {
      width: 67%;
    }

    @include breakpoint($mq-md) {
      width: 70%;
    }
  }
}

.enabled-indicator {
  border-radius: 50%;
  display: inline-block;
  height: 0.5rem;
  margin: 0 0 0 0.5rem;
  transform: translateY(-1px);
  vertical-align: middle;
  width: 0.5rem;

  &--enabled {
    background-color: green;
  }

  &--disabled {
    background-color: red;
  }
}

.background-color-chip,
.radial-gradient-color-chip {
  display: inline-block;
  height: 1em;
  margin: 0 0 0 0.5rem;
  transform: translateY(-1px);
  vertical-align: middle;
  width: 1em;
}
</style>
